import { useCallback, useEffect, useMemo, useState } from 'react';
import Raven from 'raven-js';
import { getProductUpdateIdxDB, getUnprocessedPUIdLocalStorage } from '../data/local-data/storeProvider';
/**
 * superstore does not support version updating yet, so we will need to work around this.
 * Use IDB_VERSION to handle the versioning
 * Bump the version when the schema changes. Bumping the version will cause the store to be cleared.
 */
const IDB_VERSION = 'v1';
/**
 * v1 schema:
 * type Schema = {
 *   version: string;
 *   productUpdateId: number;
 *   stageDate: number;
 *   isViewed: boolean;
 * }
 */

const getIdxKey = (userId, productUpdateId) => `${userId}-${productUpdateId}`;
const defaultIdxValue = {
  version: 'v1',
  isViewed: false
};
const usePUViewedIdxDB = (userId, startStageDate) => {
  const LOCAL_STORAGE_KEY = 'unprocessedPUIdLocalStorage';
  const [idxDB, setIdxDB] = useState();
  const [idxUpdatedTime, setIdxUpdatedTime] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isUserIdProvided = useMemo(() => userId !== undefined, [userId]);
  const unprocessedPUIdLocalStorage = useMemo(() => {
    if (!userId) {
      return null;
    }
    return getUnprocessedPUIdLocalStorage(userId);
  }, [userId]);
  useEffect(() => {
    if (!idxDB && userId) {
      setIsLoading(true);
      getProductUpdateIdxDB().then(_idxDB => {
        setIdxDB(_idxDB);
      }).catch(err => {
        Raven.captureMessage('Initiate PU indexedDB error', {
          extra: {
            userId,
            error: err
          }
        });
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [idxDB, userId]);
  const setup = useCallback(async allAfterStartDatePUs => {
    if (!idxDB) {
      return Promise.reject(new Error('Required local DB are not initiated'));
    }
    const fetchedPUIds = new Set(allAfterStartDatePUs.map(pu => pu.id));
    const newPUStageDates = allAfterStartDatePUs.reduce((acc, pu) => acc.set(pu.id, pu.stageDate), new Map());
    const existedPUs = new Set();
    const toBeDeletedPUKeys = new Set();
    await idxDB.getAllRecords((key, _idxPU) => {
      const idxPU = _idxPU;
      // exclude other users' data
      if (!key.includes(`${userId}`)) {
        return;
      }
      if (!fetchedPUIds.has(idxPU.productUpdateId) || idxPU.stageDate <= startStageDate || idxPU.version !== IDB_VERSION || idxPU.stageDate !== newPUStageDates.get(idxPU.productUpdateId)) {
        toBeDeletedPUKeys.add(key);
      } else {
        existedPUs.add(idxPU.productUpdateId);
      }
    });

    // delete old or non-existed PUs
    await Promise.all(Array.from(toBeDeletedPUKeys).map(key => idxDB.delete(key)));

    //add new PUs
    await Promise.all(allAfterStartDatePUs.filter(pu => !existedPUs.has(pu.id)).map(pu => {
      const value = Object.assign({}, defaultIdxValue, {
        productUpdateId: pu.id,
        stageDate: pu.stageDate
      });
      return idxDB.set(getIdxKey(userId, pu.id), value);
    }));
    return Promise.resolve();
  }, [idxDB, startStageDate, userId]);
  const getAllUnviewedPUIds = useCallback(async () => {
    if (!idxDB) {
      return Promise.resolve(new Set());
    }
    const unviewedPUIds = new Set();
    await idxDB.getAllRecords((key, _localPU) => {
      const localPU = _localPU;
      if (!localPU.isViewed && key.startsWith(userId)) {
        unviewedPUIds.add(localPU.productUpdateId);
      }
    });
    return Promise.resolve(unviewedPUIds);
    // add idxUpdatedTime to work around the issue that the idxDB is not updated immediately(temporary dead zone)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idxDB, userId, idxUpdatedTime]);
  const schedulePUViewed = useCallback(puId => {
    if (!unprocessedPUIdLocalStorage) {
      return;
    }
    let unprocessedPUIds = [];
    if (unprocessedPUIdLocalStorage.getItem(LOCAL_STORAGE_KEY) === null) {
      unprocessedPUIdLocalStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([]));
    }
    unprocessedPUIds = JSON.parse(unprocessedPUIdLocalStorage.getItem(LOCAL_STORAGE_KEY));
    if (!unprocessedPUIds.includes(puId)) {
      unprocessedPUIdLocalStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([...unprocessedPUIds, puId]));
    }
  }, [unprocessedPUIdLocalStorage]);
  const processAllScheduledViewedPUs = useCallback(async () => {
    if (!idxDB || !unprocessedPUIdLocalStorage) {
      return Promise.resolve();
    }
    let unprocessedPUIds = [];
    if (unprocessedPUIdLocalStorage.getItem(LOCAL_STORAGE_KEY) === null) {
      unprocessedPUIdLocalStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([]));
    }
    unprocessedPUIds = JSON.parse(unprocessedPUIdLocalStorage.getItem(LOCAL_STORAGE_KEY));
    if (unprocessedPUIds.length === 0) {
      return Promise.resolve();
    }
    const unprocessedPUIdSet = new Set(unprocessedPUIds);
    await Promise.all(unprocessedPUIds.map(puId => {
      const key = getIdxKey(userId, puId);
      return idxDB.get(key).then(async _puInIdx => {
        const puInIdx = _puInIdx;
        // skip if the PU was deleted
        if (!_puInIdx) {
          return Promise.resolve();
        }
        // update PUs
        await idxDB.set(key, Object.assign({}, puInIdx, {
          isViewed: true
        })).catch(() => {});
        unprocessedPUIdSet.delete(puId);
      }).catch(() => {
        Raven.captureMessage('update isViewed for puId failed', {
          extra: {
            puId
          }
        });
      });
    })).then(() => {
      // remove all PUIds from local storage
      unprocessedPUIdLocalStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([]));
      // update idxUpdatedTime to trigger creating new version of getAllUnviewedPUIds
      setIdxUpdatedTime(Date.now());
    }).catch(() => {
      Raven.captureMessage('update isViewed for following puIds failed', {
        extra: {
          puIds: Array.from(unprocessedPUIds)
        }
      });
    });
    return Promise.resolve();
  }, [idxDB, unprocessedPUIdLocalStorage, userId]);
  return useMemo(() => {
    return {
      isSupported: isUserIdProvided && Boolean(idxDB),
      isLoading,
      setup,
      getAllUnviewedPUIds,
      schedulePUViewed,
      processAllScheduledViewedPUs
    };
  }, [isUserIdProvided, idxDB, isLoading, setup, getAllUnviewedPUIds, schedulePUViewed, processAllScheduledViewedPUs]);
};
export default usePUViewedIdxDB;